import { formatDistanceToNow } from 'date-fns';

// Parses a "used/total" memory string (integer MB) into numbers
const parseMemParts = (memString?: string): { used: number; total: number } | null => {
  if (!memString) return null;
  const [used, total] = memString.split('/').map(Number);
  if (isNaN(used) || isNaN(total) || total === 0) return null;
  return { used, total };
};

// Formats memory from MB to GB and calculates the percentage
export const formatMemory = (memString: string) => {
  const parts = parseMemParts(memString);
  if (!parts) {
    return { used: '0', total: '0', percentage: '0' };
  }
  const usedGB = (parts.used / 1024).toFixed(1);
  const totalGB = (parts.total / 1024).toFixed(1);
  const percentage = ((parts.used / parts.total) * 100).toFixed(1);
  return { used: usedGB, total: totalGB, percentage };
};

export interface MemoryUsage {
  used: string; // GB, one decimal
  total: string; // GB, one decimal
  percentage: string; // effective (or fallback) usage as % of total, one decimal
  /** Effective usage fraction (0-100). Key colors/alerts off this only — high cache is healthy. */
  effectivePercent: number;
  /** Used + reclaimable cache/buffers extent (0-100), null when the agent doesn't report it. */
  htopPercent: number | null;
}

// Builds the memory display model. Prefers mem_effective (total - MemAvailable) over the raw
// free "used" column, which files shared memory (tmpfs, /dev/shm, shm segments) under cache
// and drastically under-reports on hosts with large shared memory. Falls back to mem for
// older agents that don't send the newer fields.
export const formatMemoryUsage = (mem: string, memEffective?: string, memHtop?: string): MemoryUsage => {
  const effective = parseMemParts(memEffective);
  const primary = effective ?? parseMemParts(mem);
  if (!primary) {
    return { used: '0', total: '0', percentage: '0', effectivePercent: 0, htopPercent: null };
  }
  const effectivePercent = Math.min((primary.used / primary.total) * 100, 100);
  // Only render the cache segment when effective data is available (single-segment fallback otherwise)
  const htop = effective ? parseMemParts(memHtop) : null;
  const htopPercent = htop
    ? Math.min(Math.max((htop.used / htop.total) * 100, effectivePercent), 100)
    : null;
  return {
    used: (primary.used / 1024).toFixed(1),
    total: (primary.total / 1024).toFixed(1),
    percentage: effectivePercent.toFixed(1),
    effectivePercent,
    htopPercent,
  };
};

// Formats a UNIX timestamp into a relative time string (e.g., "about 5 hours ago")
export const formatUptime = (timestamp: number, options?: { addSuffix?: boolean, includeSeconds?: boolean }) => {
  return formatDistanceToNow(new Date(timestamp * 1000), options);
};

// Returns a Tailwind CSS background color class based on the time since last seen
export const getStatusColor = (timeSinceLastSeen: number) => {
  if (timeSinceLastSeen > 180) return 'bg-red-500'; // Offline
  if (timeSinceLastSeen > 90) return 'bg-yellow-500'; // Warning
  return 'bg-green-500'; // Online
};

// Parses a size string (e.g., "500GB") into a number in GB
export const parseSize = (size: string): number => {
  const match = size.match(/^([\d.]+)([KMGT])?B?$/i);
  if (!match) return 0;
  const [, num, unit] = match;
  const value = parseFloat(num);
  switch (unit?.toUpperCase()) {
    case 'T': return value * 1024;
    case 'G': return value;
    case 'M': return value / 1024;
    case 'K': return value / (1024 * 1024);
    default: return value;
  }
};

// Normalizes hostnames like 'srv01' to 'server01' for consistent lookups
export const normalizeHostname = (hostname: string): string => {
  const match = hostname.match(/^srv(\d+)$/i);
  if (match) {
    const number = match[1].padStart(2, '0');
    return `server${number}`;
  }
  return hostname;
};

// The application uses this for both normalizing and displaying hostnames
export const formatDisplayHostname = (hostname: string): string => {
    return normalizeHostname(hostname);
};
